/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { Element as ReactScrollElement } from 'react-scroll';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';

import { Typography } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Faqs from 'bundles/design-system/components/Faqs';
import type { Maybe } from 'bundles/premium-hub/__generated__/baseContentfulTypes';
import { filterNullOrUndefined } from 'bundles/premium-hub/utils';
import type { UniversityProgramPageContentTestimonialsCollection } from 'bundles/xddp/__generated__/baseContentfulTypes';
import AboutProgram from 'bundles/xddp/components/homepage/AboutProgram';
import GetStartedSection from 'bundles/xddp/components/homepage/GetStartedSection';
import ProgramOverview from 'bundles/xddp/components/homepage/ProgramOverview';
import RelatedNews from 'bundles/xddp/components/homepage/RelatedNews';
import StackabilityInfo from 'bundles/xddp/components/homepage/StackabilityInfo';
import TestimonialSection from 'bundles/xddp/components/homepage/TestimonialSection';
import UpcomingEvents from 'bundles/xddp/components/homepage/UpcomingEvents';
import OverviewPageSchemaMarkup from 'bundles/xddp/components/seo/OverviewPageSchemaMarkup';
import AtAGlanceSection from 'bundles/xddp/components/shared/AtAGlanceSection';
import Container from 'bundles/xddp/components/shared/Container';
import VideoStory from 'bundles/xddp/components/shared/VideoStory';
import { MAX_FAQS_TO_SHOW } from 'bundles/xddp/constants';
import type { DegreeSubPageProps, UpsellsByDegreeSlug, UserAgentProps } from 'bundles/xddp/types';

import _t from 'i18n!nls/xddp';

const styles: Record<string, Interpolation<Theme>> = {
  faqHeader: css`
    font-size: 2.125rem;
    line-height: 2.625rem;
  `,
};

type Props = UserAgentProps &
  DegreeSubPageProps & {
    listOfUpsellsLinkedtoThisDegree?: UpsellsByDegreeSlug;
  };

const convertTestimonialsForProps = (
  testimonialsCollection?: Maybe<UniversityProgramPageContentTestimonialsCollection>
) => {
  const collection: UniversityProgramPageContentTestimonialsCollection = {
    total: -1,
    skip: -1,
    limit: -1,
    items: [],
  };

  if (testimonialsCollection?.items) {
    const items = testimonialsCollection?.items.filter(filterNullOrUndefined);
    collection.items = items;
    collection.total = items.length;
  }

  return collection;
};

const OverviewPage: React.FC<Props> = (props) => {
  const {
    pageContent,
    userAgent,
    listOfUpsellsLinkedtoThisDegree,
    personalizedDdpData,
    handlePersonalizedRequestInfoClick,
  } = props;
  const { programName, partnerInformation, testimonialsCollection, expressionOfInterestContainer, slug } = pageContent;
  const bannerVideo = partnerInformation?.bannerVideo;

  return (
    <div className="rc-OverviewPage">
      <AtAGlanceSection {...pageContent.atAGlance} slug={pageContent.slug as string} />
      <OverviewPageSchemaMarkup pageContent={pageContent} />
      <ProgramOverview
        userAgent={userAgent}
        marketingContent={pageContent.programHighlights}
        atAGlance={pageContent.atAGlance}
        listOfUpsellsLinkedtoThisDegree={listOfUpsellsLinkedtoThisDegree}
        personalizedDdpData={personalizedDdpData}
        handlePersonalizedRequestInfoClick={handlePersonalizedRequestInfoClick}
        programName={programName}
        partnerName={partnerInformation?.partnerName}
        expressionOfInterestContainer={expressionOfInterestContainer}
        slug={slug}
      />

      {!!testimonialsCollection?.items && (
        <TestimonialSection
          testimonialsCollection={convertTestimonialsForProps(
            testimonialsCollection as UniversityProgramPageContentTestimonialsCollection
          )}
        />
      )}

      {!!listOfUpsellsLinkedtoThisDegree?.length && (
        <ReactScrollElement name="xddpStackabilityInfoScrollerAnchor">
          <StackabilityInfo stackableItems={listOfUpsellsLinkedtoThisDegree || []} userAgent={userAgent} />
        </ReactScrollElement>
      )}
      <GetStartedSection marketingContent={pageContent.stackabilityInfo} userAgent={userAgent} />
      <AboutProgram
        header={pageContent.aboutProgramHeader}
        marketingContentList={pageContent.aboutProgramCollection?.items}
      />

      <UpcomingEvents userAgent={userAgent} upcomingEvents={pageContent.upcomingEventsCollection?.items} />
      {partnerInformation?.bannerVideo && (
        <Container>
          <VideoStory
            image={bannerVideo?.image}
            videoEmbedUrl={bannerVideo?.videoEmbedUrl}
            entryTitle={bannerVideo?.entryTitle}
            authorName={bannerVideo?.authorName}
            authorTitle={bannerVideo?.authorTitle}
            text={bannerVideo?.text}
          />
        </Container>
      )}
      <RelatedNews relatedNews={pageContent.relatedNewsCollection?.items} />
      <div className="bg-light-blue rc-CenteredHeaderSection">
        <Container>
          <Typography css={styles.faqHeader} variant="h2">
            {pageContent.faqsHeader || _t('Frequently Asked Questions')}
          </Typography>
          <Faqs
            faqs={pageContent.faqsCollection?.items}
            enableShowMoreList
            showMoreListProps={{
              numItemsToShow: MAX_FAQS_TO_SHOW,
              controllerProps: { itemTypePlural: _t('frequently asked questions') },
            }}
          />
        </Container>
      </div>
    </div>
  );
};

export default compose<Props, Props>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'xddp',
      page: 'overview_page',
    },
  }))
)(OverviewPage);
